/**
 * Orders API Endpoints
 *
 * Client functions for order-related API calls.
 * These functions are used by React Query hooks.
 */

import { supabase } from "@/integrations/supabase/client";
import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type { CheckOrderStatusResponse } from "../types/orders";

/**
 * Check the status of an order/checkout
 *
 * @param checkoutId - The checkout session ID to check
 * @returns Promise with order status and session_id
 * @throws {Error} If the response format is invalid or checkout_id is missing
 *
 * @example
 * const status = await checkOrderStatus("cs_test_abc123");
 * if (status.is_completed) {
 *   console.log(`Order completed! Session ID: ${status.session_id}`);
 * }
 */
export async function checkOrderStatus(
  checkoutId: string
): Promise<CheckOrderStatusResponse> {
  if (!checkoutId) {
    throw new Error("checkout_id is required");
  }

  const { data, error } = await supabase.functions.invoke(
    `api/orders/check-status?checkout_id=${encodeURIComponent(checkoutId)}`,
    {
      method: "GET",
    }
  );

  // Handle errors (network issues, 500s, etc.)
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || typeof data.is_completed !== "boolean") {
    console.error("Invalid order status response:", data);
    throw new Error(
      "Invalid response format from orders/check-status endpoint"
    );
  }

  return data;
}

export async function simulatePurchase(checkoutId: string): Promise<void> {
  const { error } = await supabase.functions.invoke(
    "api/orders/simulate-purchase",
    {
      method: "POST",
      body: { checkout_id: checkoutId },
    }
  );

  await handleSupabaseFunctionError(error);
}
